import React, { useState } from "react";
import { BaseDto } from "../../types/Rows";
import { Link } from "react-router-dom";

interface ExpandableItemsListProps {
  data: (BaseDto & {
    oldValues?: string;
    newValues?: string;
    tableName: string;
    resourceId: string;
    correlationId: string;
  })[];
  isLoading: boolean;
  onItemClick?: (item: BaseDto) => void;
  urlPrefix: string;
}

const ExpandableItemsList: React.FC<ExpandableItemsListProps> = ({
  data,
  isLoading,
  onItemClick,
  urlPrefix,
}) => {
  const [expandedItemId, setExpandedItemId] = useState<string | null>(null);

  const handleExpandClick = (id: string) => {
    setExpandedItemId((prev) => (prev === id ? null : id));
  };

  const highlightDifferences = (oldValues: string, newValues: string) => {
    const oldEntries = oldValues ? JSON.parse(oldValues) : {};
    const newEntries = newValues ? JSON.parse(newValues) : {};

    // Get all unique keys from both old and new values
    const allKeys = new Set([...Object.keys(oldEntries), ...Object.keys(newEntries)]);
    
    const changedKeys = Array.from(allKeys).filter((key) => {
      const oldValue = oldEntries[key] || "";
      const newValue = newEntries[key] || "";
      return oldValue !== newValue;
    });

    const unchangedKeys = Array.from(allKeys).filter((key) => {
      const oldValue = oldEntries[key] || "";
      const newValue = newEntries[key] || "";
      return oldValue === newValue && (oldValue || newValue);
    });

    return (
      <div className="space-y-4">
        {/* Changed Values - Side by Side */}
        {changedKeys.length > 0 && (
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-red-50 p-3 rounded border-l-4 border-red-400">
              <h5 className="font-semibold text-red-800 mb-2">Old Values:</h5>
              {changedKeys.map((key) => (
                <p key={key} className="mb-1">
                  <strong>{key}:</strong>{" "}
                  <span className="text-red-700">{oldEntries[key] || "—"}</span>
                </p>
              ))}
            </div>
            <div className="bg-[#EDF6FF] p-3 rounded border-l-4 border-[#3B82F6]">
              <h5 className="font-semibold text-[#1D4ED8] mb-2">New Values:</h5>
              {changedKeys.map((key) => (
                <p key={key} className="mb-1">
                  <strong>{key}:</strong>{" "}
                  <span className="font-bold text-[#2563EB]">{newEntries[key] || "—"}</span>
                </p>
              ))}
            </div>
          </div>
        )}

        {/* Unchanged Values */}
        {unchangedKeys.length > 0 && (
          <div className="bg-[var(--sc-bg)] p-3 rounded border-l-4 border-[var(--sc-muted)]">
            <h5 className="font-semibold text-[var(--sc-text)] mb-2">Unchanged Values:</h5>
            {unchangedKeys.map((key) => (
              <p key={key} className="mb-1">
                <strong>{key}:</strong>{" "}
                <span className="text-[var(--sc-muted)]">{newEntries[key] || oldEntries[key] || "—"}</span>
              </p>
            ))}
          </div>
        )}
      </div>
    );
  };

  if (isLoading) return <div>Loading...</div>;

  // Get the first item's correlationId for the top button
  const firstItemCorrelationId = data[0]?.correlationId;

  return (
    <>
      <div className="m-2 text-sm font-bold text-[var(--sc-text)]">
        <div className="flex justify-between items-center">
          <div>
            <p>Created: {data[0]?.created.toLocaleString()}</p>
            <p>Created By: {data[0]?.createdBy}</p>
          </div>
          {firstItemCorrelationId && (
            <Link
              to={`/audit/correlation/${firstItemCorrelationId}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center px-3 py-2 text-sm leading-4 font-medium rounded-lg bg-[var(--sc-text)] text-[var(--sc-surface)] hover:opacity-80 focus:outline-none focus:ring-1 focus:ring-[var(--sc-amber)]"
            >
              Link
            </Link>
          )}
        </div>
      </div>
      <div className="grid gap-4">
        {data.map((item) => (
          <div key={item.id} className="rounded-[10px] border border-[var(--sc-border)] p-4">
            <div
              className="cursor-pointer hover:shadow-lg"
              onClick={() => {
                handleExpandClick(item.id);
                if (onItemClick) onItemClick(item);
              }}
            >
              <h3 className="text-sm font-semibold text-[var(--sc-text)]">{item.name}</h3>
              <div className="text-sm text-[var(--sc-muted)]">{`${item.description}`}</div>

              {item.newValues && (
                <>
                  <div className="text-sm text-[var(--sc-muted)]">{`Id ${item.resourceId}`}</div>

                  <div className="text-sm text-[var(--sc-muted)]">{`Table ${item.tableName}`}</div>

                  <hr className="my-2 border-[var(--sc-border)]" />

                  <div className="text-sm text-[var(--sc-muted)]">
                    {item.oldValues || item.newValues
                      ? highlightDifferences(
                          item.oldValues || "",
                          item.newValues || ""
                        )
                      : item.description}
                  </div>
                </>
              )}
            </div>
          </div>
        ))}
      </div>
    </>
  );
};

export default ExpandableItemsList;
